import {ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  CustomerGroup, FileService, ListTable, NgxEasyTablePagination, ProductCustomer,
  StoreService, WarehouseGroup
} from 'compob2b-libs';
import {APIDefinition, Columns} from 'compob2b-table';
import {takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {SearchAutocompleteComponent} from '../../search-autocomplete/search-autocomplete.component';
import {AddToRequestedProductsPopupComponent} from '../add-to-requested-products-popup/add-to-requested-products-popup.component';
import {ModalProductComponent} from '../modal-product/modal-product.component';
import {ProductService} from '../services/product.service';
import { DeviceService } from '../../../../../../compob2b-libs/src/lib/services/device.service';

@Component({
  selector: 'app-customer-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ProductListComponent extends ListTable implements OnInit {

  @Input() searchKeyword: string | null = '';
  @Input() productList: ProductCustomer[];
  @Input() pagination: NgxEasyTablePagination;
  @Input() shortView = false;
  @Input() select = false;
  @Input() customCartTpl: TemplateRef<any>;
  @Input() isShowcase = false;

  readonly customerGroup$ = this.storeService.getCustomerGroup();
  productService = inject(ProductService);

  @ViewChild('table') productTable: ElementRef;
  @ViewChild('photoTpl', {static: true}) photoTpl: TemplateRef<any>;
  @ViewChild('codeIconTpl', {static: true}) codeIconTpl: TemplateRef<any>;
  @ViewChild('headerTpl', {static: true}) headerTpl: TemplateRef<any>;
  @ViewChild('wmsTpl', {static: true}) wmsTpl: TemplateRef<any>;
  @ViewChild('priceTpl', {static: true}) priceTpl: TemplateRef<any>;
  @ViewChild('priceNdsTpl', {static: true}) priceNdsTpl: TemplateRef<any>;
  @ViewChild('cartTpl', {static: true}) cartTpl: TemplateRef<any>;
  @ViewChild('deliveryAtpTpl', {static: true}) deliveryAtpTpl: TemplateRef<any>;
  @ViewChild('table', {static: true}) table: APIDefinition;
  @ViewChild(SearchAutocompleteComponent) autocomplete: SearchAutocompleteComponent;

  constructor(
    readonly fileService: FileService,
    readonly device: DeviceService,
    readonly storeService: StoreService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.configuration.orderEventOnly = true;
    this.configuration.orderEnabled = !this.shortView;
    this.configuration.paginationEnabled = !this.shortView;

    this.customerGroup$.pipe(tap(data => this.handleCustomerGroup(data)), takeUntil(this.unsubscribe)).subscribe();
  }

  handleCustomerGroup(customerGroup: CustomerGroup) {
    this.productService.handleCustomerGroup(customerGroup);
    if (customerGroup) {
      this.columns = this.initTableColumns(customerGroup.warehouseGroup);
      this.tableService.initFrom(this);
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  protected initTableColumns(warehouses: WarehouseGroup[]): Columns[] {
    const columns: Columns[] = [];
    columns.push(
      {key: 'productCode', title: 'Код товара', cellTemplate: this.codeIconTpl, orderEnabled: true}
    );
    columns.push(
      {key: 'photo', title: translations.photo, cellTemplate: this.photoTpl}
    );
    columns.push(
      {
        key: 'header',
        title: translations.articulHeaderShowcase,
        cellTemplate: this.headerTpl,
        orderEnabled: true
      }
    );

    columns.push(
      {key: 'price', title: translations.priceRur, orderEnabled: true, cellTemplate: this.priceTpl, readonly: true}
    );
    columns.push(
      {key: 'priceNds', title: translations.priceNdsRur, orderEnabled: true, cellTemplate: this.priceNdsTpl, readonly: true}
    );

    if (!this.isShowcase && warehouses !== undefined && warehouses.length) {

      warehouses.forEach(warehouse => {
          if ((warehouse.header === 'Поставщик' && this.shortView) || (!this.shortView)) {
            columns.push({
              key: 'balancesOnGroupsOfWarehouses.' + warehouse.id,
              title: warehouse.header,
              cellTemplate: this.wmsTpl,
              orderEnabled: true
            });
          }
        }
      );
    }

    columns.push(
      {
        key: 'deliveryAtp',
        title: 'Дата готовности',
        cellTemplate: this.deliveryAtpTpl,
        orderEnabled: true
      }
    );

    if (!this.isShowcase) {
      columns.push(
        {key: 'to_cart', title: translations.amount, cellTemplate: this.cartTpl, readonly: true}
      );
    }

    return columns;
  }

  public getImageUri(picture: string) {
    return picture && picture != '.' ? this.fileService.getImageSrc(picture, 800) : this.noPhotoSrc;
  }

  openAddToRequestedProducts(product: ProductCustomer): void {
    this.windowService.open(AddToRequestedProductsPopupComponent, {
      context: {
        product,
        amount: 1
      }
    });
  }

  openModalProduct(product: ProductCustomer): void {
    if (!product) return
    if (this.device.isMobile) {
      this.router.navigate(['/product/', product.syncUid]);
    } else {
      const uid = product.sync === false ? product.syncUidPim : product.syncUid;
      this.productService.openModalProduct(ModalProductComponent, uid, this.productList);
    }
  }

  isShowRequestedProducts(product: ProductCustomer) : boolean {
    return this.productService.isShowRequestedProducts(product);
  }

  protected readonly translations = translations;
}
